// src/components/rituals/WeeklyRituals.tsx
// Sunday Reset and Wednesday Check-in rituals

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, CheckCircle, ChevronLeft, X, Check, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { useSoulFile } from '../../hooks/useSoulFile';

type RitualType = 'reset' | 'check-in';
type Step = 'intro' | 'intention' | 'focus' | 'wins' | 'adjustments' | 'reflection' | 'complete';

interface WeeklyRitualProps {
  type: RitualType;
  onComplete: () => void;
  onClose: () => void;
  embedded?: boolean;
}

export function WeeklyRitual({ type, onComplete, onClose, embedded = false }: WeeklyRitualProps) {
  const { user } = useAuth();
  const { soulFile } = useSoulFile();

  const isReset = type === 'reset';
  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [saving, setSaving] = useState(false);

  // Form state
  const [weekIntention, setWeekIntention] = useState('');
  const [focusAreas, setFocusAreas] = useState<string[]>(['', '', '']);
  const [wins, setWins] = useState<string[]>(['', '', '']);
  const [adjustments, setAdjustments] = useState('');
  const [reflection, setReflection] = useState('');

  const steps: Step[] = isReset
    ? ['intro', 'intention', 'focus', 'complete']
    : ['intro', 'wins', 'adjustments', 'reflection', 'complete'];

  const wrapperClass = embedded
    ? `flex-1 overflow-y-auto ${
        isReset
          ? 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
          : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'
      }`
    : `min-h-screen ${
        isReset
          ? 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
          : 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'
      }`;

  const getWeekStart = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = now.getDate() - dayOfWeek;
    return new Date(now.setDate(diff)).toISOString().split('T')[0];
  };

  const handleNext = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handleSaveAndComplete = async () => {
    if (!user?.id) return;
    
    setSaving(true);

    try {
      const weekStart = getWeekStart();
      
      const data = isReset ? {
        user_id: user.id,
        ritual_type: 'reset',
        week_start: weekStart,
        week_intention: weekIntention.trim() || null,
        focus_areas: focusAreas.filter(f => f.trim()),
      } : {
        user_id: user.id,
        ritual_type: 'check-in',
        week_start: weekStart,
        wins: wins.filter(w => w.trim()),
        adjustments: adjustments.trim() || null,
        midweek_reflection: reflection.trim() || null,
      };

      await supabase.from('weekly_rituals').insert(data);

      // Log activity
      await supabase.from('activity_log').insert({
        user_id: user.id,
        activity_type: isReset ? 'weekly-reset' : 'weekly-check-in',
        activity_date: new Date().toISOString().split('T')[0],
      });

      setCurrentStep('complete');
    } catch (err) {
      console.error('Error saving ritual:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateFocusArea = (index: number, value: string) => {
    const newAreas = [...focusAreas];
    newAreas[index] = value;
    setFocusAreas(newAreas);
  };

  const updateWin = (index: number, value: string) => {
    const newWins = [...wins];
    newWins[index] = value;
    setWins(newWins);
  };

  return (
    <div className={wrapperClass}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={onClose}
          className={`p-2 rounded-full ${isReset ? 'text-indigo-600 hover:bg-indigo-100' : 'text-teal-600 hover:bg-teal-100'}`}
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="flex items-center gap-2">
          {isReset ? (
            <RefreshCw className="w-5 h-5 text-indigo-500" />
          ) : (
            <CheckCircle className="w-5 h-5 text-teal-500" />
          )}
          <span className={`font-medium ${isReset ? 'text-indigo-700' : 'text-teal-700'}`}>
            {isReset ? 'Sunday Reset' : 'Wednesday Check-in'}
          </span>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5">
          {steps.map((step, i) => (
            <div 
              key={step}
              className={`w-2 h-2 rounded-full transition-colors ${
                steps.indexOf(currentStep) >= i 
                  ? (isReset ? 'bg-indigo-500' : 'bg-teal-500')
                  : (isReset ? 'bg-indigo-200' : 'bg-teal-200')
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 pt-8 pb-20">
        <AnimatePresence mode="wait">
          {/* INTRO */}
          {currentStep === 'intro' && (
            <StepWrapper key="intro">
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
                    isReset ? 'bg-indigo-100' : 'bg-teal-100'
                  }`}
                >
                  {isReset ? (
                    <RefreshCw className="w-10 h-10 text-indigo-500" />
                  ) : (
                    <CheckCircle className="w-10 h-10 text-teal-500" />
                  )}
                </motion.div>
                
                <h1 className={`text-2xl font-bold mb-3 ${isReset ? 'text-indigo-900' : 'text-teal-900'}`}>
                  {isReset ? 'Sunday Reset' : 'Wednesday Check-in'}
                  {soulFile?.essence.preferredName ? `, ${soulFile.essence.preferredName}` : ''} ✨
                </h1>
                
                <p className={`mb-8 ${isReset ? 'text-indigo-600' : 'text-teal-600'}`}>
                  {isReset 
                    ? "Let's set the tone for your week ahead."
                    : "Let's check in on your week so far."
                  }
                </p>

                <button
                  onClick={handleNext}
                  className={`px-8 py-3 rounded-xl font-medium text-white ${
                    isReset 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500'
                      : 'bg-gradient-to-r from-teal-500 to-emerald-500'
                  }`}
                >
                  Begin
                </button>
              </div>
            </StepWrapper>
          )}

          {/* RESET: INTENTION */}
          {currentStep === 'intention' && isReset && (
            <StepWrapper key="intention">
              <h2 className="text-xl font-semibold text-indigo-900 mb-2">
                Set Your Week Intention
              </h2>
              <p className="text-indigo-600 mb-6">
                What's the one thing you want to embody or achieve this week?
              </p>
              
              <textarea
                value={weekIntention}
                onChange={(e) => setWeekIntention(e.target.value)}
                placeholder="This week, I intend to..."
                rows={4}
                className="w-full p-4 rounded-xl border-2 border-indigo-200 focus:border-indigo-400 focus:ring-0 outline-none transition-colors resize-none mb-6"
              />

              <button
                onClick={handleNext}
                disabled={!weekIntention.trim()}
                className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-medium disabled:opacity-50"
              >
                Continue
              </button>
            </StepWrapper>
          )}

          {/* RESET: FOCUS AREAS */}
          {currentStep === 'focus' && isReset && (
            <StepWrapper key="focus">
              <h2 className="text-xl font-semibold text-indigo-900 mb-2">
                Focus Areas
              </h2>
              <p className="text-indigo-600 mb-6">
                What 3 areas deserve your energy this week?
              </p>
              
              <div className="space-y-3 mb-6">
                {focusAreas.map((area, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-medium text-sm">{i + 1}</span>
                    </div>
                    <input
                      type="text"
                      value={area}
                      onChange={(e) => updateFocusArea(i, e.target.value)}
                      placeholder={`Focus area ${i + 1}...`}
                      className="flex-1 p-3 rounded-xl border border-indigo-200 focus:border-indigo-400 outline-none"
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={handleSaveAndComplete}
                disabled={saving || focusAreas.every(a => !a.trim())}
                className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Complete Reset ✨'}
              </button>
            </StepWrapper>
          )}

          {/* CHECK-IN: WINS */}
          {currentStep === 'wins' && !isReset && (
            <StepWrapper key="wins">
              <h2 className="text-xl font-semibold text-teal-900 mb-2">
                Celebrate Your Wins
              </h2>
              <p className="text-teal-600 mb-6">
                What went well this week? Even small wins count.
              </p>
              
              <div className="space-y-3 mb-6">
                {wins.map((win, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-600">🎉</span>
                    </div>
                    <input
                      type="text"
                      value={win}
                      onChange={(e) => updateWin(i, e.target.value)}
                      placeholder={`Win ${i + 1}...`}
                      className="flex-1 p-3 rounded-xl border border-teal-200 focus:border-teal-400 outline-none"
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={handleNext}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-medium"
              >
                Continue
              </button>
            </StepWrapper>
          )}

          {/* CHECK-IN: ADJUSTMENTS */}
          {currentStep === 'adjustments' && !isReset && (
            <StepWrapper key="adjustments">
              <h2 className="text-xl font-semibold text-teal-900 mb-2">
                Any Adjustments?
              </h2>
              <p className="text-teal-600 mb-6">
                Is there anything you want to do differently for the rest of the week?
              </p>
              
              <textarea
                value={adjustments}
                onChange={(e) => setAdjustments(e.target.value)}
                placeholder="I want to adjust..."
                rows={4}
                className="w-full p-4 rounded-xl border-2 border-teal-200 focus:border-teal-400 focus:ring-0 outline-none transition-colors resize-none mb-6"
              />

              <div className="flex gap-3">
                <button
                  onClick={handleNext}
                  className="flex-1 py-3 bg-teal-100 text-teal-700 rounded-xl font-medium"
                >
                  Skip
                </button>
                <button
                  onClick={handleNext}
                  className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-medium"
                >
                  Continue
                </button>
              </div>
            </StepWrapper>
          )}

          {/* CHECK-IN: REFLECTION */}
          {currentStep === 'reflection' && !isReset && (
            <StepWrapper key="reflection">
              <h2 className="text-xl font-semibold text-teal-900 mb-2">
                Midweek Reflection
              </h2>
              <p className="text-teal-600 mb-6">
                How are you feeling about your progress?
              </p>
              
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                placeholder="Right now I'm feeling..."
                rows={4}
                className="w-full p-4 rounded-xl border-2 border-teal-200 focus:border-teal-400 focus:ring-0 outline-none transition-colors resize-none mb-6"
              />

              <button
                onClick={handleSaveAndComplete}
                disabled={saving}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Complete Check-in ✨'}
              </button>
            </StepWrapper>
          )}

          {/* COMPLETE */}
          {currentStep === 'complete' && (
            <StepWrapper key="complete">
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
                    isReset ? 'bg-indigo-100' : 'bg-teal-100'
                  }`}
                >
                  <Check className={`w-10 h-10 ${isReset ? 'text-indigo-600' : 'text-teal-600'}`} />
                </motion.div>

                <h2 className={`text-2xl font-bold mb-3 ${isReset ? 'text-indigo-900' : 'text-teal-900'}`}>
                  {isReset ? 'Week Reset Complete! 🚀' : 'Check-in Complete! ✅'}
                </h2>
                
                <p className={`mb-8 ${isReset ? 'text-indigo-600' : 'text-teal-600'}`}>
                  {isReset 
                    ? "You're set up for an intentional week ahead."
                    : "Great job taking time to reflect and adjust."
                  }
                </p>

                <button
                  onClick={onComplete}
                  className={`px-8 py-3 rounded-xl font-medium text-white ${
                    isReset 
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500'
                      : 'bg-gradient-to-r from-teal-500 to-emerald-500'
                  }`}
                >
                  Done
                </button>
              </div>
            </StepWrapper>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function StepWrapper({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

export default WeeklyRitual;
